import React, { ReactElement, useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetQuizQuery,
} from '@/modules/services';
import PlayerUi from '@/components/Player';
import { useIsMobileScreenSize } from '@/lib/util';
import { Spinner } from 'elmo-elements';
import PreviewFeedbackResultRoutes, {
  PreviewFeedbackResultParamType,
} from './route';
import MyLibraryRoutes from '../MyLibrary/MyLibraryRoutes';
import QuizResult from '@/components/Player/Quiz/components/QuizResult';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';
import { FailBehaviour } from '@/modules/types/choice';

const PreviewFeedbackResult = (): ReactElement => {
  const history = useHistory();
  const { elearningId, type } = useParams<PreviewFeedbackResultParamType>();
  const elearning = useGetElearningQuery(elearningId);
  const quiz = useGetQuizQuery(elearningId);
  const {
    data: elearningBehaviourSettingsData,
    isLoading: elearningBehaviourSettingsIsLoading,
  } = useGetElearningBehaviourSettingsQuery();

  const { isMobile } = useIsMobileScreenSize();

  useThemeStyle(elearning.data?.theme);
  useModuleStyle(elearning.data);

  useEffect(() => {
    // Route parameters validation
    if (elearning.isLoading || quiz.isLoading) return;

    switch (true) {
      case !elearning.isSuccess || !quiz.isSuccess:
        // On invalid elearningId or quizId re-route to my-library
        history.replace(generatePath(MyLibraryRoutes.path));
        break;

      case type !== 'pass' && type !== 'retry' && type !== 'fail':
        // On invalid type - use correct as default feedback preview
        history.replace(
          generatePath(PreviewFeedbackResultRoutes.path, {
            elearningId,
            type: 'pass',
          }),
        );
        break;

      default:
        break;
    }
  }, [elearning, quiz, elearningId]);

  if (quiz.isLoading || elearningBehaviourSettingsIsLoading || !quiz.data) {
    return <Spinner />;
  }

  return (
    <PlayerUi
      isPreview={true}
      isPlayable={true}
      currentFrameId=""
      elearning={elearning.data}
      isQuizEnabled={false}
      frames={[]}
      resources={[]}
      helperContent={[]}
      helperFaq={[]}
      isMobileView={isMobile}
      elearningBehaviourSettings={elearningBehaviourSettingsData}
      canvas={
        <QuizResult
          score={type === 'pass' ? 100 : 99}
          pass={100}
          handler={{
            tryAgainSubmitHandler: () => {},
            restartSubmitHandler: () => {},
          }}
          content={{
            passContent: quiz.data.lmsFeedbackMetadata.lmsContent1,
            failContent: quiz.data.lmsFeedbackMetadata.lmsFailContent,
            retryContent: quiz.data.lmsFeedbackMetadata.lmsRetryContent,
          }}
          quizMetadata={{
            ...quiz.data.quizMetadata,
            failBehaviour:
              type === 'fail' ? FailBehaviour.fail : FailBehaviour.restart,
            lmsTrackingEnable: true,
          }}
          quizAttemptResponse={{
            id: 0,
            lastQuizAttemptTime: 0,
            remainingQuizAttempt: type === 'retry' ? 1 : 0,
            isAttemptFeatureEnabled: true,
          }}
        />
      }
    />
  );
};

export default PreviewFeedbackResult;
